import PropTypes from "prop-types";
import React, { useCallback } from "react";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { Button } from "@yoast/ui-library";
import { TrashIcon } from "@heroicons/react/20/solid";
import formatDate from "./formatDate";

const messages = defineMessages( {
	deleteConfirm: {
		id: "passkeys.deleteConfirm",
		defaultMessage: "Are you sure you want to remove this passkey?",
	},
	added: {
		id: "passkeys.added",
		defaultMessage: "Added { date }",
	},
	lastUsed: {
		id: "passkeys.lastUsed",
		defaultMessage: "Last used { date }",
	},
	deleteLabel: {
		id: "passkeys.deleteLabel",
		defaultMessage: "Remove passkey { name }",
	},
} );

/**
 * A single passkey row with a delete button.
 *
 * @param {Object} props The component props.
 *
 * @returns {ReactElement} The rendered passkey row.
 */
const PasskeyRow = ( { passkey, onDelete, intl } ) => {
	const handleDelete = useCallback( () => {
		// eslint-disable-next-line no-alert
		if ( window.confirm( intl.formatMessage( messages.deleteConfirm ) ) ) {
			onDelete( passkey.id );
		}
	}, [ intl, onDelete, passkey.id ] );

	return (
		<div className="yst-flex yst-items-center yst-justify-between yst-py-3 yst-border-b yst-border-slate-200">
			<div className="yst-flex-1">
				<div className="yst-font-medium">{ passkey.name }</div>
				<div className="yst-text-sm yst-text-slate-500">
					<FormattedMessage
						{ ...messages.added }
						values={ { date: formatDate( passkey.createdAt ) } }
					/>
					{ passkey.lastUsedAt && (
						<>
							{ " " }&middot;{ " " }
							<FormattedMessage
								{ ...messages.lastUsed }
								values={ { date: formatDate( passkey.lastUsedAt ) } }
							/>
						</>
					) }
				</div>
			</div>
			<div className="yst-flex yst-gap-2">
				<Button
					variant="secondary"
					size="small"
					onClick={ handleDelete }
					aria-label={ intl.formatMessage( messages.deleteLabel, { name: passkey.name } ) }
				>
					<TrashIcon className="yst-w-4 yst-h-4" />
				</Button>
			</div>
		</div>
	);
};

PasskeyRow.propTypes = {
	passkey: PropTypes.shape( {
		id: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		createdAt: PropTypes.string.isRequired,
		lastUsedAt: PropTypes.string,
	} ).isRequired,
	onDelete: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

export default injectIntl( PasskeyRow );
